import type {
  HeartbeatObservationKind,
  MicCheckHost,
  MicReport,
  ProblemCause,
  ProblemLayer,
  ResolvedBy,
} from './types'

/** The Soundcheck implementation version stamped on every telemetry row. */
export const SOUNDCHECK_VERSION = '1'

/**
 * The evidence a confirmed problem carries into telemetry, so a hidden
 * lower-priority failure is still readable.
 */
export type ProblemEvidence = {
  speechMS?: number
  serverConfirmations?: number
  msSincePause?: number
  soloMS?: number
  heartbeatObservation?: HeartbeatObservationKind | 'none'
  secretaryPresent?: boolean
}

/**
 * Everything Soundcheck records. The controller calls these; the concrete
 * implementation adds the meeting envelope and routes through
 * `meeting.logEventPassive` so automated health reporting never extends the
 * student's idle timeout. Tests inject a recording stub.
 */
export type SoundcheckTelemetry = {
  micResult: (
    host: MicCheckHost,
    report: MicReport,
    msToResult: number | null
  ) => void
  problem: (
    layer: ProblemLayer,
    cause: ProblemCause,
    shown: boolean,
    evidence: ProblemEvidence
  ) => void
  resolved: (
    layer: ProblemLayer,
    cause: ProblemCause,
    secondsOpen: number,
    resolvedBy: ResolvedBy
  ) => void
  incidentEnded: (
    reason: 'recovered' | 'left',
    incidentSeconds: number,
    openCauses: string
  ) => void
  observation: (
    kind: HeartbeatObservationKind,
    phase: 'started' | 'ended',
    detail: {
      seconds?: number
      secretaryPresent: boolean
      heartbeatAgeMS: number | null
      packetsDelta: number
      audibleDelta: number
      decodeErrorsDelta: number
    }
  ) => void
  problemSelected: (
    previousCause: ProblemCause | 'none',
    cause: ProblemCause | 'none'
  ) => void
  dialogShown: (
    cause: ProblemCause,
    source: 'auto' | 'banner' | 'notifications_enabled'
  ) => void
  dialogDismissed: (cause: ProblemCause, incidentSeconds: number) => void
  repairResult: (
    cause: ProblemCause,
    republished: boolean,
    errorName: string,
    errorMessageSanitized: string
  ) => void
}

/** A telemetry sink that does nothing, for hosts without a meeting. */
export const NOOP_TELEMETRY: SoundcheckTelemetry = {
  micResult: () => {},
  problem: () => {},
  resolved: () => {},
  incidentEnded: () => {},
  observation: () => {},
  problemSelected: () => {},
  dialogShown: () => {},
  dialogDismissed: () => {},
  repairResult: () => {},
}

/** The passive logger the concrete telemetry writes through. */
export type PassiveLogger = (
  name: string,
  params?: Record<string, unknown>
) => void

/**
 * Build a telemetry sink bound to a meeting's passive logger. `envelope`
 * supplies the fields common to every row that the caller can compute lazily:
 * the current track SID and mic state.
 */
export function createSoundcheckTelemetry(
  logEventPassive: PassiveLogger,
  envelope: () => { trackSid: string | null; micState: string }
): SoundcheckTelemetry {
  const base = () => {
    const { trackSid, micState } = envelope()
    return {
      soundcheck_version: SOUNDCHECK_VERSION,
      track_sid: trackSid ?? '',
      mic_state: micState,
    }
  }

  return {
    micResult: (host, report, msToResult) => {
      logEventPassive('soundcheck_mic_result', {
        ...base(),
        host,
        state: report.state,
        reason:
          report.state === 'failed'
            ? report.cause
            : report.state === 'checking' || report.state === 'inactive'
              ? report.reason
              : '',
        speech_detection:
          report.state === 'ready' ? report.speechDetection : '',
        has_heard_speech:
          report.state === 'ready' ? String(report.hasHeardSpeech) : '',
        ms_to_result: msToResult ?? '',
      })
    },
    problem: (layer, cause, shown, evidence) => {
      logEventPassive('soundcheck_problem', {
        ...base(),
        cause,
        layer,
        shown: String(shown),
        speech_ms: evidence.speechMS ?? '',
        server_confirmations: evidence.serverConfirmations ?? '',
        ms_since_pause: evidence.msSincePause ?? '',
        solo_ms: evidence.soloMS ?? '',
        heartbeat_observation: evidence.heartbeatObservation ?? 'none',
        secretary_present:
          evidence.secretaryPresent === undefined
            ? ''
            : String(evidence.secretaryPresent),
      })
    },
    resolved: (layer, cause, secondsOpen, resolvedBy) => {
      logEventPassive('soundcheck_resolved', {
        ...base(),
        cause,
        layer,
        seconds_open: secondsOpen,
        resolved_by: resolvedBy,
      })
    },
    incidentEnded: (reason, incidentSeconds, openCauses) => {
      logEventPassive('soundcheck_incident_ended', {
        ...base(),
        reason,
        incident_seconds: incidentSeconds,
        open_causes: openCauses,
      })
    },
    observation: (kind, phase, detail) => {
      logEventPassive('soundcheck_observation', {
        ...base(),
        kind,
        phase,
        seconds: detail.seconds ?? '',
        secretary_present: String(detail.secretaryPresent),
        heartbeat_age_ms: detail.heartbeatAgeMS ?? '',
        packets_delta: detail.packetsDelta,
        audible_delta: detail.audibleDelta,
        decode_errors_delta: detail.decodeErrorsDelta,
      })
    },
    problemSelected: (previousCause, cause) => {
      logEventPassive('soundcheck_problem_selected', {
        ...base(),
        previous_cause: previousCause,
        cause,
      })
    },
    dialogShown: (cause, source) => {
      logEventPassive('soundcheck_dialog_shown', { ...base(), cause, source })
    },
    dialogDismissed: (cause, incidentSeconds) => {
      logEventPassive('soundcheck_dialog_dismissed', {
        ...base(),
        cause,
        incident_seconds: incidentSeconds,
      })
    },
    repairResult: (cause, republished, errorName, errorMessageSanitized) => {
      logEventPassive('soundcheck_repair_result', {
        ...base(),
        cause,
        republished: String(republished),
        error_name: errorName,
        error_message_sanitized: errorMessageSanitized,
      })
    },
  }
}
